import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useNavigate, useSearchParams } from 'react-router-dom'

import { Card } from '@loadsmart/miranda-react'

import { TOKEN_STORAGE_USER_KEY } from 'auth/domain/User'
import { getStoredUser } from 'auth/infra/StoredUser'
import { useLogin } from 'auth/ui/hooks/useLogin'
import { AnalyticsEvent } from 'core/analytics/events'
import useAnalytics from 'core/analytics/useAnalytics'
import { LocalStorageClient } from 'core/infra/storage/LocalStorageClient'

import LoginForm from './LoginForm'
import { StyledCardDiv } from './LoginPage.styles'

const LoginPage = () => {
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const hasAuthenticatedRef = useRef(false)
  const hasStartedAuthRef = useRef(false)
  const abandonTrackedRef = useRef(false)
  const authTrackedRef = useRef(false)
  const startTrackedRef = useRef(false)

  // TODO: see why useAbortController usage is failing
  const abortController = useMemo(() => new AbortController(), [])

  const { isLoading, login } = useLogin()

  const storage = new LocalStorageClient()
  const userAccessToken = storage.get(TOKEN_STORAGE_USER_KEY)

  const [searchParams] = useSearchParams()

  const redirectTo = searchParams.get('redirect_to')

  function getUserData() {
    const user = getStoredUser(storage)
    return user?.email ? { email: user.email } : null
  }

  // Track when login page/form is opened
  useEffect(() => {
    if (!startTrackedRef.current) {
      startTrackedRef.current = true
      const userData = getUserData()
      track(AnalyticsEvent.Authentication, { 
        authFlow: 'Legacy', 
        authStage: 'start',
        ...(userData ? { email: userData.email } : {})
      })
    }
  }, [track])

  useEffect(() => {
    if (userAccessToken) {
      hasAuthenticatedRef.current = true
      navigate(redirectTo ?? '/demo')
    }
  }, [navigate, userAccessToken, redirectTo])

  // Track abandon if component unmounts or user navigates away without successful authentication
  useEffect(() => {
      const handleAbandon = () => {
        if (startTrackedRef.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
          abandonTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'Legacy', 
            authStage: 'abandon',
            ...(userData ? { email: userData.email } : {})
          })
        }
      }

      // Track on page visibility change (user switches tabs/windows)
      const handleVisibilityChange = () => {
        if (document.hidden && startTrackedRef.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
          abandonTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'Legacy', 
            authStage: 'abandon',
            ...(userData ? { email: userData.email } : {})
          })
        }
      }

      // Track on page unload (user navigates away/closes tab)
      const handleBeforeUnload = () => {
        if (startTrackedRef.current && !hasAuthenticatedRef.current && !abandonTrackedRef.current) {
          abandonTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'Legacy', 
            authStage: 'abandon',
            ...(userData ? { email: userData.email } : {})
          })
        }
      }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      handleAbandon()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [track])

  const handleLogin = useCallback(
    async (email: string, password: string, captchaToken: string) => {
      hasStartedAuthRef.current = true
      
      try {
        await login(email, password, captchaToken, abortController)
        hasAuthenticatedRef.current = true
        if (!authTrackedRef.current) {
          authTrackedRef.current = true
          const userData = getUserData()
          track(AnalyticsEvent.Authentication, { 
            authFlow: 'Legacy', 
            authStage: 'success',
            ...(userData ? { email: userData.email } : {})
          })
        }
      } catch (error: any) {
        const errorMessage = error?.message || error?.response?.data?.detail || 'Unknown error'
        const userData = getUserData()
        track(AnalyticsEvent.Authentication, { 
          authFlow: 'Legacy',
          authStage: 'failure',
          error: errorMessage,
          ...(userData ? { email: userData.email } : {})
        })
        setError('Login failed. Please check your credentials.')
      }
    },
    [abortController, login, track]
  )

  return (
    <StyledCardDiv>
      <Card>
        <Card.Title>Sign in to Loadsmart's Analytics Portal</Card.Title>
        <Card.Divider></Card.Divider>
        <Card.Body>
          {error && <p>{error}</p>}
          <LoginForm onSubmit={handleLogin} isLoading={isLoading} />
        </Card.Body>
      </Card>
    </StyledCardDiv>
  )
}

export default LoginPage
